<template>
  <v-card class="mx-auto overflow-hidden w-100" max-width="500">
    
    <v-card-title class="d-flex flex-column align-center text-center px-6 pt-6 pb-2">
      <v-icon class="text-orange-lighten-2 mb-2" size="72px">
        mdi-alert-circle-outline
      </v-icon>
      <div class="text-h6 font-weight-bold">
        未確認の請求書があります
      </div>
    </v-card-title>

    <div class="px-6 pb-2">
      <div class="text-grey-darken-1" 
           style="font-size: 9px; 
                  line-height: 1.6; 
                  font-weight: normal; 
                  text-align: left; 
                  word-break: break-all;
                  display: block;">
        請求書のご確認をお願いいたします。<br />
        内容をご確認いただくまで、このお知らせが繰り返し表示されます。
      </div>
    </div>

    <div class="px-6 pb-4">
      <template v-if="!loading && !isSlected">
        <v-list v-if="invoices.length > 0" class="pa-0" bg-color="transparent">
          <v-list-item
            v-for="(i, index) in invoices"
            :key="index"
            @click="toDetail(i)"
            class="px-0"
            min-height="64px"
          >
            <template v-slot:prepend>
              <v-avatar size="40" class="me-3">
                <v-img :src="i.imageUrl" />
              </v-avatar>
            </template>

            <v-list-item-title class="text-body-2" style="white-space: pre-line">{{ i.text }}</v-list-item-title>

            <template v-slot:append>
              <v-badge
                color="red"
                offset-x="4"
                offset-y="0"
                inline
              ></v-badge>
            </template>
          </v-list-item>
        </v-list>
        
        <div v-else class="text-center text-grey pa-6">
          未確認の請求書はありません。
        </div>

        <v-card-actions class="d-flex justify-center pt-6">
          <v-btn
            color="pink-lighten-2"
            class="text-white"
            variant="flat"
            @click="$emit('close')"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </template>
      <div
        v-else
        class="d-flex justify-center align-center text-center"
        style="height: 100px; position: relative;"
      >
        <vue-loading
          loader="bars"
          v-model:active="isLoading"
          type="grid"
          color="#EE6090"
          :width="50"
          :height="50"
          :is-full-page="false"
        </vue-loading>
      </div>
    </div>
  </v-card>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import rules from '@/service/ValidRules';

import { chatModule } from '@/store/entityModules/chat';
import { familyModule } from '@/store/entityModules/family';
import { invoiceModule } from '@/store/entityModules/invoice';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import { VForm } from '@/model/Vuetify/VForm';
import { PostHomeAbsenceRequest } from '@/model/Chat';

interface DataType {
  isLoading: boolean,
  loading: boolean,
  invoices: any[],
  children: any[],
  dates: string[];
  fetchLoading: boolean;
  btnLoading: boolean;
  isSlected: boolean;
}

export default defineComponent({
  components: {
    'vue-loading': Loading,
  },

  name: 'InvoiceModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    family() {
      return familyModule.family || null;
    },
    rules() {
      return rules;
    },
    form(): VForm {
      return (this.$refs as any).post_absence_form;
    },
    allowMinDate(): string {
      return dayjs().format('YYYY-MM-DD');
    },
    dateRangeText() {
      return this.dates.join(' ~ ');
    },
  },

  watch: {
    isOpen: {
      async handler(val: boolean) {
        if (val) {
          this.dates = [];
        }
      },
      deep: true,
    },
  },

  data: (): DataType => ({
    isLoading: true,
    invoices: [],
    children: [],
    dates: [],
    fetchLoading: false,
    btnLoading: false,
    loading: true,
    isSlected: false,
  }),

  methods: {
    async toDetail(invoice: any) {
      this.isSlected = true;
      await invoiceModule.fetchInvoices({ childId: invoice.childId, year: null });
      await invoiceModule.selectInvoice(invoice);
      this.isSlected = false;
      this.$router.push({ name: 'InvoiceDetail' });
    },
    close() {
      this.$emit('close');
    },
  },

  async mounted() {
    this.loading = true;

    if (!this.family || !this.family.children) {
      this.loading = false;
      return;
    }

    // 1. family.children の処理を map で並列化し、Promise.all で待機
    const childInvoicesPromises = this.family.children.map(async (child: any) => {
      // childId のデータ型をチェックし、常に数値に変換します
      let childId: number;
      if (typeof child.childId === 'number') {
        childId = child.childId;
      } else if (typeof child.childId === 'string') {
        childId = Number(child.childId);
      } else {
        // childId が無効な場合は処理をスキップ
        return []; 
      }

      // 請求書データを取得し、待機
      await invoiceModule.fetchInvoices({ childId: childId, year: null });

      // 取得した請求書データをフィルタリングし、整形して返す
      if (!invoiceModule.invoices) {
        return [];
      }

      var displayNameText = child.lastName + ' ' + child.firstName;

      // 2. 未読の請求書をフィルタリングし、新しい形式にマップ
      const newInvoices = invoiceModule.invoices
        .filter((invoice: any) => invoice.readDate === null)
        .map((invoice: any) => {
          var displayText = "";
          displayText = (invoice.year) + '年度' + invoice.month + '月';

          // 取得したinvoiceデータに必要な情報を加えて返す
          return {
            ...invoice,
            imageUrl: child.imageUrl,
            childId: child.childId,
            text: displayNameText + "\n" + displayText,
          };
        })
        .sort((a: any, b: any) => {
          // ソートキーを作成: 1, 2, 3月は +12 して翌年扱いにする
          const aMonth = a.month <= 3 ? a.month + 12 : a.month;
          const bMonth = b.month <= 3 ? b.month + 12 : b.month;

          // まず年度で比較
          if (a.year !== b.year) {
            return a.year - b.year;
          }

          // 次に調整した月で比較 (1-3月が13-15となり、4-12月の後になる)
          return aMonth - bMonth;
        });

        return newInvoices;
      });

      // 3. 全ての非同期処理が完了するのを待ち、結果をフラット化して this.invoices にセット
      const allInvoices = await Promise.all(childInvoicesPromises);
      this.invoices = allInvoices.flat();
      this.loading = false;
    },
  });
</script>
<style lang="scss" scoped>
</style>
